/**
 * LotSmith — Settings page.
 *
 * Shows: account email + tier · device list (label / broker / last seen
 * with a per-device "Revoke" action) · sign out.
 *
 * Devices come from `GET /v1/devices` on worker-license. Revoke calls
 * `POST /v1/devices/:id/revoke`. Sign out calls `POST /v1/auth/logout`
 * on worker-auth, then redirects to /login.
 *
 * If `VITE_LICENSE_API_BASE` isn't set (offline / preview), the device
 * list shows a small notice and skips the network call so the page is
 * still navigable.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDev } from "../providers/dataContext.js";
import { EmptyState, ErrorState, Skeleton } from "../components/States.js";

interface DeviceRow {
  id: string;
  label: string;
  eaVersion: string;
  brokerLogin: string | null;
  brokerServer: string | null;
  registeredAt: string;
  lastSeenAt: string | null;
}

type LoadState = "loading" | "ok" | "error" | "offline";

export function Settings() {
  const { user } = useDev();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const loadDevices = useCallback(async () => {
    const base = licenseApiBase();
    if (!base) {
      setState("offline");
      return;
    }
    setState("loading");
    setError(null);
    try {
      const r = await fetch(`${base}/v1/devices`, {
        credentials: "include",
        headers: { accept: "application/json" },
      });
      if (r.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      if (!r.ok) throw new Error(`devices ${r.status}`);
      const j = (await r.json()) as { devices: DeviceRow[] };
      setDevices(j.devices);
      setState("ok");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load devices.");
      setState("error");
    }
  }, [navigate]);

  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  async function revoke(id: string) {
    const base = licenseApiBase();
    if (!base) return;
    setRevokingId(id);
    try {
      const r = await fetch(`${base}/v1/devices/${id}/revoke`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`revoke ${r.status}`);
      setDevices((list) => list.filter((d) => d.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't revoke device.");
    } finally {
      setRevokingId(null);
    }
  }

  async function signOut() {
    const base = authApiBase();
    setSigningOut(true);
    try {
      if (base) {
        await fetch(`${base}/v1/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
      }
    } catch {
      // Logout is best-effort — even if the request fails, send the user
      // back to the login screen so they can sign in again. The session
      // cookie is short-lived enough that an unreachable worker-auth
      // doesn't leave them stuck.
    }
    navigate("/login", { replace: true });
  }

  return (
    <div className="dash">
      <section className="route-hero">
        <div>
          <div className="dash__eyebrow">Workspace controls</div>
          <h1 className="dash__title">Settings</h1>
          <p className="route-hero__lead">Manage devices, risk defaults, notifications, connected channels, and account data.</p>
        </div>
        <div className="route-hero__stats">
          <div className="route-stat">
            <span>Plan</span>
            <strong className="tone-profit">{user.tier}</strong>
          </div>
          <div className="route-stat route-stat--wide">
            <span>Signed in</span>
            <strong>{user.email}</strong>
          </div>
        </div>
      </section>

      <nav className="settings-toc" aria-label="Settings sections">
        <a href="#account">Account</a>
        <a href="#trading">Trading</a>
        <a href="#preferences">Preferences</a>
        <a href="#data">Data</a>
      </nav>

      <h2 id="trading" className="settings-group">Trading</h2>

      <section className="panel">
        <div className="panel__hdr">
          <h2 className="panel__title">Devices</h2>
          <button
            type="button"
            className="panel__action"
            onClick={() => void loadDevices()}
            disabled={state === "loading"}
          >
            Refresh
          </button>
        </div>

        {state === "loading" && (
          <div style={{ padding: 16 }}>
            <Skeleton height="18px" lines={3} />
          </div>
        )}

        {state === "offline" && (
          <EmptyState
            title="Live data unavailable"
            body="Set VITE_LICENSE_API_BASE in the web build to connect to your worker-license deployment."
          />
        )}

        {state === "error" && error && (
          <ErrorState
            title="Couldn't load devices"
            body={error}
            cta={{ label: "Try again", onClick: () => void loadDevices() }}
          />
        )}

        {state === "ok" && devices.length === 0 && (
          <EmptyState
            title="No devices registered"
            body="Install LotSmith.ex5 on MT5 and paste your device token to register your first device."
            cta={{ label: "Onboarding", onClick: () => { window.location.href = "/onboarding"; } }}
          />
        )}

        {state === "ok" && devices.length > 0 && (
          <ul className="device-list">
            {devices.map((d) => (
              <li key={d.id} className="device-list__row">
                <div className="device-list__main">
                  <div className="device-list__label">{d.label}</div>
                  <div className="device-list__meta">
                    {d.brokerLogin && d.brokerServer
                      ? `#${d.brokerLogin} · ${d.brokerServer}`
                      : d.brokerLogin ?? d.brokerServer ?? "—"}
                    {" · "}
                    EA {d.eaVersion}
                    {" · "}
                    {d.lastSeenAt ? `last seen ${formatRelative(d.lastSeenAt)}` : "never seen"}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => void revoke(d.id)}
                  disabled={revokingId === d.id}
                >
                  {revokingId === d.id ? "Revoking…" : "Revoke"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <RiskDefaultsSection />

      <h2 id="account" className="settings-group">Account</h2>

      <SubscriptionSection user={user} />

      <h2 id="preferences" className="settings-group">Preferences</h2>

      <TelegramSection />

      <DiscordSection />

      <NotificationsSection />

      <h2 id="data" className="settings-group">Data</h2>

      <DangerZoneSection />

      <section className="panel">
        <div className="panel__hdr">
          <h2 className="panel__title">Session</h2>
        </div>
        <div className="panel__body" style={{ padding: 16 }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void signOut()}
            disabled={signingOut}
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </section>
    </div>
  );
}

// ── Subscription / Upgrade card ────────────────────────────────────
interface SubProps { user: { tier: string }; }
function SubscriptionSection({ user }: SubProps) {
  const base = billingApiBase();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function startCheckout(tier: "PRO" | "TRADER", cadence: "monthly" | "annual"): Promise<void> {
    if (!base) {
      setError("Billing not configured. Set VITE_BILLING_API_BASE in the web build.");
      return;
    }
    setBusy(`${tier}-${cadence}`);
    setError(null);
    try {
      const r = await fetch(`${base}/v1/checkout/session`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tier, cadence }),
      });
      if (!r.ok) throw new Error(`checkout ${r.status}`);
      const j = await r.json() as { url: string };
      window.location.href = j.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't start checkout.");
      setBusy(null);
    }
  }

  async function openBillingPortal(): Promise<void> {
    if (!base) {
      setError("Billing not configured. Set VITE_BILLING_API_BASE in the web build.");
      return;
    }
    setBusy("portal");
    setError(null);
    try {
      const r = await fetch(`${base}/v1/checkout/portal`, { credentials: "include" });
      if (r.status === 409) {
        setError("No active Stripe subscription on this account yet.");
        setBusy(null);
        return;
      }
      if (!r.ok) throw new Error(`portal ${r.status}`);
      const j = await r.json() as { url: string };
      window.location.href = j.url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't open billing portal.");
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      <div className="panel__hdr">
        <h2 className="panel__title">Subscription</h2>
        <span className="panel__action">{user.tier}</span>
      </div>
      <div className="panel__body" style={{ padding: 16 }}>
        {error && <div className="form-error">{error}</div>}
        {user.tier === "FREE" && (
          <div className="sub">
            <p className="sub__lead">
              Upgrade to unlock cloud sync, Telegram alerts, prop firm rule
              engine, and full history.
            </p>
            <div className="sub__row">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void startCheckout("PRO", "monthly")}
                disabled={busy !== null}
              >
                {busy === "PRO-monthly" ? "Redirecting…" : "Pro · $12/mo"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void startCheckout("PRO", "annual")}
                disabled={busy !== null}
              >
                {busy === "PRO-annual" ? "Redirecting…" : "Pro · $99/yr"}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void startCheckout("TRADER", "monthly")}
                disabled={busy !== null}
              >
                {busy === "TRADER-monthly" ? "Redirecting…" : "Trader · $29/mo"}
              </button>
            </div>
            <p className="sub__fine">
              14-day refund. Cancel anytime. <a href="/pricing">Compare tiers</a>.
            </p>
          </div>
        )}
        {user.tier !== "FREE" && (
          <div className="sub">
            <p className="sub__lead">
              You're on <strong>{user.tier}</strong>. Manage your subscription
              (change card, cancel, switch cadence) in Stripe's billing portal.
            </p>
            <div className="sub__row">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void openBillingPortal()}
                disabled={busy !== null}
              >
                {busy === "portal" ? "Opening…" : "Manage subscription"}
              </button>
            </div>
            <p className="sub__fine">
              Or email{" "}
              <a href="mailto:support@lotsmith.app">support@lotsmith.app</a>{" "}
              if you need help.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function billingApiBase(): string {
  return (import.meta.env.VITE_BILLING_API_BASE as string | undefined) ?? "";
}

// ── Notifications matrix ──────────────────────────────────────────
type NotifEvent = "daily_loss_approaching" | "trade_open" | "trade_close" | "eod_summary" | "rule_breach" | "weekly_review";
type NotifChannel = "telegram" | "email" | "webpush";
type NotifPrefs = Record<NotifEvent, Partial<Record<NotifChannel, boolean>>>;

const NOTIF_EVENTS: Array<{ id: NotifEvent; label: string; defaults: Partial<Record<NotifChannel, boolean>> }> = [
  { id: "daily_loss_approaching", label: "Daily loss approaching", defaults: { telegram: true, webpush: true } },
  { id: "trade_open",             label: "Trade open",              defaults: {} },
  { id: "trade_close",            label: "Trade close",             defaults: {} },
  { id: "eod_summary",            label: "End-of-day summary",      defaults: { email: true } },
  { id: "rule_breach",            label: "Rule breach",             defaults: { telegram: true, email: true, webpush: true } },
  { id: "weekly_review",          label: "Weekly review (Pro)",     defaults: { email: true } },
];
const NOTIF_CHANNELS: Array<{ id: NotifChannel; label: string }> = [
  { id: "telegram", label: "Telegram" },
  { id: "email",    label: "Email" },
  { id: "webpush",  label: "Web push" },
];

function NotificationsSection() {
  const base = licenseApiBase();
  const [prefs, setPrefs] = useState<NotifPrefs>(emptyPrefs());
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!base) { setLoaded(true); return; }
    void (async () => {
      try {
        const r = await fetch(`${base}/v1/preferences`, { credentials: "include" });
        if (!r.ok) throw new Error(`prefs ${r.status}`);
        const j = await r.json() as { notifications: Record<string, Record<string, boolean>> };
        setPrefs(mergeWithDefaults(j.notifications ?? {}));
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Couldn't load preferences.");
        setPrefs(emptyPrefs());
      } finally {
        setLoaded(true);
      }
    })();
  }, [base]);

  function toggle(event: NotifEvent, channel: NotifChannel) {
    setPrefs((p) => ({
      ...p,
      [event]: { ...p[event], [channel]: !p[event][channel] },
    }));
  }

  async function save() {
    if (!base || saving) return;
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${base}/v1/preferences`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ notifications: prefs }),
      });
      if (!r.ok) throw new Error(`save ${r.status}`);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  if (!base) return null;

  return (
    <section className="panel">
      <div className="panel__hdr">
        <h2 className="panel__title">Notifications</h2>
        <span className="panel__action">
          {savedAt && Date.now() - savedAt < 4000 ? "Saved" : ""}
        </span>
      </div>
      <div className="panel__body" style={{ padding: 16 }}>
        {!loaded && <Skeleton height="18px" lines={4} />}
        {err && <div className="form-error">{err}</div>}
        {loaded && (
          <>
            <table className="notif-matrix">
              <thead>
                <tr>
                  <th />
                  {NOTIF_CHANNELS.map((c) => <th key={c.id}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {NOTIF_EVENTS.map((e) => (
                  <tr key={e.id}>
                    <td className="notif-matrix__label">{e.label}</td>
                    {NOTIF_CHANNELS.map((c) => (
                      <td key={c.id} className="notif-matrix__cell">
                        <input
                          type="checkbox"
                          aria-label={`${e.label} via ${c.label}`}
                          checked={!!prefs[e.id][c.id]}
                          onChange={() => toggle(e.id, c.id)}
                          disabled={saving}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => void save()} disabled={saving}>
                {saving ? "Saving…" : "Save preferences"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function emptyPrefs(): NotifPrefs {
  const out = {} as NotifPrefs;
  for (const e of NOTIF_EVENTS) out[e.id] = { ...e.defaults };
  return out;
}

function mergeWithDefaults(stored: Record<string, Record<string, boolean>>): NotifPrefs {
  const out = emptyPrefs();
  for (const e of NOTIF_EVENTS) {
    const s = stored[e.id];
    if (s) {
      for (const c of NOTIF_CHANNELS) {
        if (typeof s[c.id] === "boolean") out[e.id][c.id] = s[c.id];
      }
    }
  }
  return out;
}

// ── Risk defaults ──────────────────────────────────────────────────
function RiskDefaultsSection() {
  const base = licenseApiBase();
  const [vals, setVals] = useState({ riskPct: 0.5, slPips: 20, tpPips: 40 });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!base) { setLoaded(true); return; }
    void (async () => {
      try {
        const r = await fetch(`${base}/v1/preferences`, { credentials: "include" });
        if (!r.ok) throw new Error(`prefs ${r.status}`);
        const j = await r.json() as { riskDefaults: { riskPct?: number; slPips?: number; tpPips?: number } };
        setVals({
          riskPct: typeof j.riskDefaults?.riskPct === "number" ? j.riskDefaults.riskPct : 0.5,
          slPips:  typeof j.riskDefaults?.slPips  === "number" ? j.riskDefaults.slPips  : 20,
          tpPips:  typeof j.riskDefaults?.tpPips  === "number" ? j.riskDefaults.tpPips  : 40,
        });
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Couldn't load risk defaults.");
      } finally {
        setLoaded(true);
      }
    })();
  }, [base]);

  async function save() {
    if (!base || saving) return;
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${base}/v1/preferences`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ riskDefaults: vals }),
      });
      if (!r.ok) throw new Error(`save ${r.status}`);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  }

  if (!base) return null;

  return (
    <section className="panel">
      <div className="panel__hdr">
        <h2 className="panel__title">Risk defaults</h2>
        <span className="panel__action">
          {savedAt && Date.now() - savedAt < 4000 ? "Saved" : ""}
        </span>
      </div>
      <div className="panel__body" style={{ padding: 16 }}>
        {!loaded && <Skeleton height="18px" lines={2} />}
        {err && <div className="form-error">{err}</div>}
        {loaded && (
          <>
            <p style={{ color: "var(--text-secondary)", marginTop: 0 }}>
              Pre-fill values for the EA panel. Override per-trade as needed.
            </p>
            <div className="risk-defaults">
              <label>
                Risk %
                <input
                  type="number" step={0.1} min={0} max={5}
                  className="input num" value={vals.riskPct}
                  onChange={(e) => setVals({ ...vals, riskPct: Number(e.target.value) || 0 })}
                />
              </label>
              <label>
                SL pips
                <input
                  type="number" step={1} min={0} max={500}
                  className="input num" value={vals.slPips}
                  onChange={(e) => setVals({ ...vals, slPips: Number(e.target.value) || 0 })}
                />
              </label>
              <label>
                TP pips (0 = no TP)
                <input
                  type="number" step={1} min={0} max={1000}
                  className="input num" value={vals.tpPips}
                  onChange={(e) => setVals({ ...vals, tpPips: Number(e.target.value) || 0 })}
                />
              </label>
            </div>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => void save()} disabled={saving}>
                {saving ? "Saving…" : "Save defaults"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── Danger zone ────────────────────────────────────────────────────
function DangerZoneSection() {
  const base = licenseApiBase();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  async function deleteAccount() {
    if (confirm !== "DELETE" || busy) return;
    if (!base) { setErr("License API not configured."); return; }
    if (!window.confirm("Last chance: this soft-deletes your account. Hard delete in 30 days. Proceed?")) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${base}/v1/me`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error(`delete ${r.status}`);
      const auth = (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
      if (auth) {
        await fetch(`${auth}/v1/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
      }
      navigate("/", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't delete account.");
    } finally {
      setBusy(false);
    }
  }

  if (!base) return null;

  return (
    <section className="panel panel--danger">
      <div className="panel__hdr">
        <h2 className="panel__title">Danger zone</h2>
      </div>
      <div className="panel__body" style={{ padding: 16 }}>
        <p style={{ color: "var(--text-secondary)", marginTop: 0 }}>
          Soft-deletes your account immediately. Trade + equity rows hard-delete
          30 days later (per the privacy notice). Type <code>DELETE</code> below
          to enable the button.
        </p>
        <div className="danger-row">
          <input
            type="text"
            className="input"
            placeholder="Type DELETE to confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => void deleteAccount()}
            disabled={confirm !== "DELETE" || busy}
          >
            {busy ? "Deleting…" : "Delete my account"}
          </button>
        </div>
        {err && <div className="form-error" style={{ marginTop: 8 }}>{err}</div>}
      </div>
    </section>
  );
}

// ── Telegram pairing card ──────────────────────────────────────────
interface PairingState {
  paired: boolean;
  username?: string | null;
  pairedAt?: string;
  silenced?: boolean;
}

function TelegramSection() {
  const base = licenseApiBase();
  const [pair, setPair] = useState<PairingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!base) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${base}/v1/telegram/pairing`, { credentials: "include" });
      if (!r.ok) throw new Error(`pairing ${r.status}`);
      setPair(await r.json() as PairingState);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load Telegram pairing.");
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { void load(); }, [load]);

  async function generate() {
    if (!base) return;
    setError(null);
    try {
      const r = await fetch(`${base}/v1/telegram/pair-code`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`pair-code ${r.status}`);
      setCode(await r.json() as { code: string; expiresAt: string });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't generate code.");
    }
  }

  async function disconnect() {
    if (!base) return;
    if (!confirm("Disconnect Telegram? You can re-pair anytime.")) return;
    try {
      const r = await fetch(`${base}/v1/telegram/pairing`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`disconnect ${r.status}`);
      setPair({ paired: false });
      setCode(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't disconnect.");
    }
  }

  if (!base) return null;

  return (
    <section className="panel">
      <div className="panel__hdr">
        <h2 className="panel__title">Telegram</h2>
      </div>
      <div className="panel__body" style={{ padding: 16 }}>
        {loading && <Skeleton height="18px" lines={2} />}
        {error && <div className="form-error">{error}</div>}
        {!loading && pair && pair.paired && (
          <div className="tg-pair">
            <div className="tg-pair__msg">
              ✅ Paired{pair.username ? ` with @${pair.username}` : ""}.
              {pair.silenced && " 🔕 Alerts silenced (DM /silent to re-enable)."}
            </div>
            <button type="button" className="btn btn--ghost" onClick={() => void disconnect()}>
              Disconnect
            </button>
          </div>
        )}
        {!loading && pair && !pair.paired && !code && (
          <div className="tg-pair">
            <div className="tg-pair__msg">
              Get state-change alerts and a daily digest in your Telegram inbox.
            </div>
            <button type="button" className="btn btn--primary" onClick={() => void generate()}>
              Generate pair code
            </button>
          </div>
        )}
        {code && (
          <div className="tg-pair">
            <div className="tg-pair__code">
              <strong>{code.code}</strong>
              <span className="tg-pair__exp">expires {new Date(code.expiresAt).toLocaleTimeString()}</span>
            </div>
            <ol className="tg-pair__steps">
              <li>Open your Telegram and DM <code>@LotSmithBot</code></li>
              <li>Send <code>/connect {code.code}</code></li>
              <li>Refresh this page</li>
            </ol>
            <button type="button" className="btn btn--ghost" onClick={() => { setCode(null); void load(); }}>
              I'm done
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Discord webhook card ───────────────────────────────────────────
interface DiscordState {
  configured: boolean;
  label?: string;
  updatedAt?: string;
  lastTestedAt?: string | null;
  lastError?: string | null;
}

function DiscordSection() {
  const base = licenseApiBase();
  const [state, setState] = useState<DiscordState | null>(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!base) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${base}/v1/discord/webhook`, { credentials: "include" });
      if (!r.ok) throw new Error(`discord ${r.status}`);
      setState(await r.json() as DiscordState);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load Discord webhook.");
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!base || saving) return;
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${base}/v1/discord/webhook`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!r.ok) throw new Error(r.status === 403 ? "Upgrade required for Discord alerts." : `save ${r.status}`);
      setUrl("");
      setSavedAt(Date.now());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save Discord webhook.");
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    if (!base || testing) return;
    setTesting(true); setError(null);
    try {
      const r = await fetch(`${base}/v1/discord/webhook/test`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`test ${r.status}`);
      setSavedAt(Date.now());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't send test message.");
    } finally {
      setTesting(false);
    }
  }

  async function disconnect() {
    if (!base) return;
    if (!confirm("Disconnect Discord webhook?")) return;
    try {
      const r = await fetch(`${base}/v1/discord/webhook`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`disconnect ${r.status}`);
      setState({ configured: false });
      setUrl("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't disconnect Discord.");
    }
  }

  if (!base) return null;

  return (
    <section className="panel">
      <div className="panel__hdr">
        <h2 className="panel__title">Discord</h2>
        <span className="panel__action">{savedAt && Date.now() - savedAt < 4000 ? "Saved" : ""}</span>
      </div>
      <div className="panel__body" style={{ padding: 16 }}>
        {loading && <Skeleton height="18px" lines={2} />}
        {error && <div className="form-error">{error}</div>}
        {!loading && (
          <div className="tg-pair">
            <div className="tg-pair__msg">
              {state?.configured
                ? `Connected: ${state.label ?? "Discord webhook"}`
                : "Paste a Discord channel webhook for Pro alert delivery."}
              {state?.lastError && ` Last test failed: ${state.lastError}.`}
            </div>
            <label className="label" htmlFor="discord-webhook-url">Webhook URL</label>
            <input
              id="discord-webhook-url"
              className="input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://discord.com/api/webhooks/..."
              type="password"
              autoComplete="off"
            />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => void save()} disabled={saving || url.trim() === ""}>
                {saving ? "Saving…" : state?.configured ? "Replace webhook" : "Save webhook"}
              </button>
              {state?.configured && (
                <>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => void test()} disabled={testing}>
                    {testing ? "Sending…" : "Send test"}
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => void disconnect()}>
                    Disconnect
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function licenseApiBase(): string {
  return (import.meta.env.VITE_LICENSE_API_BASE as string | undefined) ?? "";
}

function authApiBase(): string {
  return (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
}

function formatRelative(iso: string): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
